import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axiosInstance from "../utils/axiosInstance";

// ─── helpers ──────────────────────────────────────────────────────────────────
const now = () => new Date().toLocaleString("rw-RW");
const uid = () => Math.random().toString(36).slice(2, 9);
const LS_KEY = "igitabo_village_system";

const load = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || { villages: [], isibo: [], submissions: [], logs: [] }; }
  catch { return { villages: [], isibo: [], submissions: [], logs: [] }; }
};
const save = (d) => localStorage.setItem(LS_KEY, JSON.stringify(d));

const useStore = () => {
  const [store, setStore] = useState(load);
  const update = useCallback((fn) => {
    setStore((prev) => {
      const next = fn(structuredClone(prev));
      save(next);
      return next;
    });
  }, []);
  return [store, update];
};

const numVal = (v) => {
  const n = Number(v);
  return isNaN(n) || n < 0 ? null : n;
};

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
};

const printPDF = (rows, villages, grandTotals, title = "Raporo") => {
  const date = new Date().toLocaleString("rw-RW");
  const approved = rows.filter(r => r.status === "APPROVED").length;
  const submitted = rows.filter(r => r.status === "SUBMITTED").length;
  const rejected = rows.filter(r => r.status === "REJECTED").length;

  const statusBadge = (s) => {
    const map = { APPROVED: ["#166534","#dcfce7"], SUBMITTED: ["#1e40af","#dbeafe"], REJECTED: ["#991b1b","#fee2e2"] };
    const [color, bg] = map[s] || ["#374151","#f3f4f6"];
    return `<span style="background:${bg};color:${color};border-radius:12px;padding:2px 10px;font-size:11px;font-weight:700">${s}</span>`;
  };

  const tableRows = rows.map((r, i) => {
    const vname = villages.find(v => v.id === r.village_id)?.village_name || "—";
    return `<tr style="background:${i%2===0?"#fff":"#f8fafc"};">
      <td>${vname}</td><td><strong>${r.isibo_name}</strong></td>
      <td>${r.elderly_count}</td><td>${r.children_count}</td>
      <td>${r.youth_count}</td><td>${r.households_count}</td>
      <td><strong>${r.total_population}</strong></td>
      <td>${statusBadge(r.status)}</td>
      <td style="font-size:11px;color:#6b7280">${r.submitted_at || "—"}</td>
      <td style="font-size:11px">${r.created_by || "—"}</td>
    </tr>`;
  }).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1e293b; padding: 32px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #003580; padding-bottom: 16px; margin-bottom: 24px; }
    .logo { font-size: 22px; font-weight: 800; color: #003580; }
    .logo span { color: #c8a84b; }
    .meta { text-align: right; font-size: 12px; color: #6b7280; line-height: 1.6; }
    .title { font-size: 18px; font-weight: 800; color: #003580; margin-bottom: 4px; }
    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
    .stat-box { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; text-align: center; background: #f8fafc; }
    .stat-val { font-size: 22px; font-weight: 800; color: #003580; }
    .stat-lbl { font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; margin-top: 2px; }
    .status-row { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
    .pill { border-radius: 20px; padding: 4px 14px; font-size: 12px; font-weight: 700; }
    .pill-green { background: #dcfce7; color: #166534; }
    .pill-blue  { background: #dbeafe; color: #1e40af; }
    .pill-red   { background: #fee2e2; color: #991b1b; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead th { background: #003580; color: #fff; padding: 10px 12px; text-align: left; font-weight: 700; white-space: nowrap; }
    thead th:nth-child(6), thead th:nth-child(7) { text-align: right; }
    tbody td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
    tbody td:nth-child(3), tbody td:nth-child(4), tbody td:nth-child(5), tbody td:nth-child(6), tbody td:nth-child(7) { text-align: right; }
    tfoot td { background: #f1f5f9; font-weight: 800; padding: 10px 12px; border-top: 2px solid #003580; color: #003580; }
    tfoot td:nth-child(3), tfoot td:nth-child(4), tfoot td:nth-child(5), tfoot td:nth-child(6), tfoot td:nth-child(7) { text-align: right; }
    .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }
    @media print { body { padding: 16px; } @page { margin: 1.5cm; size: A4 landscape; } }
  </style></head><body>
  <div class="header">
    <div>
      <div class="logo">Igitabo <span>•</span> Akagari</div>
      <div class="title" style="margin-top:6px">${title}</div>
    </div>
    <div class="meta">
      <div><strong>Itariki:</strong> ${date}</div>
      <div><strong>Amasibo:</strong> ${rows.length}</div>
      <div><strong>Imidugudu:</strong> ${[...new Set(rows.map(r=>r.village_id))].length}</div>
    </div>
  </div>

  <div class="stats-grid">
    <div class="stat-box"><div class="stat-val">${grandTotals.population.toLocaleString()}</div><div class="stat-lbl">Abaturage Bose</div></div>
    <div class="stat-box"><div class="stat-val">${grandTotals.households.toLocaleString()}</div><div class="stat-lbl">Ingo</div></div>
    <div class="stat-box"><div class="stat-val">${grandTotals.youth.toLocaleString()}</div><div class="stat-lbl">Urubyiruko</div></div>
    <div class="stat-box"><div class="stat-val">${grandTotals.elderly.toLocaleString()}</div><div class="stat-lbl">Abakuye</div></div>
  </div>

  <div class="status-row">
    <span class="pill pill-green">✓ Yemejwe: ${approved}</span>
    <span class="pill pill-blue">⏳ Gutegereza: ${submitted}</span>
    <span class="pill pill-red">✕ Yasubijwe: ${rejected}</span>
  </div>

  <table>
    <thead><tr>
      <th>Umudugudu</th><th>Isibo</th><th>Abakuye</th><th>Abana</th>
      <th>Urubyiruko</th><th>Ingo</th><th>Abaturage</th>
      <th>Status</th><th>Yoherejweho</th><th>Uwayohereje</th>
    </tr></thead>
    <tbody>${tableRows}</tbody>
    <tfoot><tr>
      <td colspan="2">IGITERANYO</td>
      <td>${grandTotals.elderly.toLocaleString()}</td>
      <td>${grandTotals.children.toLocaleString()}</td>
      <td>${grandTotals.youth.toLocaleString()}</td>
      <td>${grandTotals.households.toLocaleString()}</td>
      <td>${grandTotals.population.toLocaleString()}</td>
      <td colspan="3"></td>
    </tr></tfoot>
  </table>

  <div class="footer">
    <span>Igitabo — Sisitemu y'Amakuru y'Umudugudu</span>
    <span>Intego: ${rows.length} isibo | ${date}</span>
  </div>
  <script>window.onload = () => { window.print(); }<\/script>
  </body></html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
};

// ─── styles ───────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,15,40,0.85)",
    backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center",
    padding: "1rem", overflowY: "auto",
  },
  card: {
    background: "linear-gradient(135deg,#001233,#002060)", border: "1px solid rgba(200,168,75,0.25)",
    borderRadius: 20, width: "100%", maxWidth: 820, padding: "2rem",
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)", position: "relative",
  },
  input: {
    width: "100%", background: "rgba(0,20,55,0.9)", border: "1px solid rgba(99,102,241,0.25)",
    borderRadius: 10, color: "#f1f5f9", padding: "10px 14px", fontSize: 15, outline: "none",
    transition: "border-color .2s",
  },
  inputErr: { borderColor: "#ef4444" },
  label: { color: "#94a3b8", fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" },
  errMsg: { color: "#ef4444", fontSize: 12, marginTop: 3 },
  btn: (color = "#003580", fill = "#1a56b0") => ({
    background: `linear-gradient(135deg,${color},${fill})`, border: "none", borderRadius: 10,
    color: "#fff", padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 14,
    transition: "opacity .2s, transform .15s", display: "inline-flex", alignItems: "center", gap: 7,
  }),
  btnSm: (color = "#003580", outline = false) => ({
    background: outline ? "transparent" : color,
    border: `1.5px solid ${color}`,
    borderRadius: 8,
    color: outline ? color : "#fff",
    padding: "5px 14px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    transition: "all .15s",
    whiteSpace: "nowrap",
  }),
  progress: {
    display: "flex", gap: 6, marginBottom: "1.5rem", alignItems: "center",
  },
  progressStep: (active, done) => ({
    width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 700, flexShrink: 0,
    background: done ? "#1a7a4a" : active ? "#003580" : "rgba(255,255,255,0.08)",
    border: done ? "2px solid #1a7a4a" : active ? "2px solid #c8a84b" : "2px solid rgba(255,255,255,0.15)",
    color: done || active ? "#fff" : "#64748b",
  }),
  progressLine: (done) => ({
    flex: 1, height: 3, borderRadius: 2,
    background: done ? "#1a7a4a" : "rgba(255,255,255,0.1)",
  }),
  badge: (color) => ({
    background: `${color}22`, border: `1px solid ${color}55`, color, borderRadius: 20,
    padding: "2px 10px", fontSize: 11, fontWeight: 700,
  }),
  toast: (type) => ({
    position: "fixed", bottom: 24, right: 24, zIndex: 99999,
    background: type === "success" ? "#1a7a4a" : type === "error" ? "#b91c1c" : "#003580",
    color: "#fff", borderRadius: 12, padding: "12px 20px", fontWeight: 600, fontSize: 14,
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", gap: 8,
    animation: "slideUp .3s ease",
  }),
  tableWrap: { overflowX: "auto", borderRadius: 12, border: "1px solid rgba(200,168,75,0.2)", marginTop: "1rem", boxShadow: "0 4px 24px rgba(0,0,0,0.3)" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 },
  th: { background: "linear-gradient(135deg,#002060,#003580)", color: "#c8a84b", padding: "11px 14px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase" },
  td: { padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#cbd5e1", verticalAlign: "middle", fontSize: 13 },
  trHover: { background: "rgba(99,102,241,0.06)" },
  summaryBox: {
    background: "rgba(0,53,128,0.18)", border: "1px solid rgba(200,168,75,0.2)",
    borderRadius: 12, padding: "1rem 1.25rem", marginTop: "1rem",
  },
  section: { marginBottom: "1.5rem" },
  heading: { color: "#c8a84b", fontWeight: 700, fontSize: 16, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 8 },
  divider: { border: "none", borderTop: "1px solid rgba(200,168,75,0.15)", margin: "1.25rem 0" },
};

// ─── toast ────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  const icons = { success: "✓", error: "✗", info: "ℹ" };
  return (
    <div style={S.toast(type)}>
      <span>{icons[type] || "ℹ"}</span>
      {msg}
    </div>
  );
};

// ─── field ────────────────────────────────────────────────────────────────────
const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: "1rem" }}>
    <label style={S.label}>{label}</label>
    {children}
    {error && <div style={S.errMsg}>⚠ {error}</div>}
  </div>
);

const Input = ({ error, ...p }) => (
  <input style={{ ...S.input, ...(error ? S.inputErr : {}) }} {...p} />
);

const NumInput = ({ error, ...p }) => (
  <input type="number" min="0" style={{ ...S.input, ...(error ? S.inputErr : {}) }} {...p} />
);

// ─── modal wrapper ─────────────────────────────────────────────────────────────
const Modal = ({ onClose, children, maxWidth = 820 }) => (
  <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
    <div style={{ ...S.card, maxWidth }}>
      {onClose && (
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
      )}
      {children}
    </div>
  </div>
);

// ─── progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ step, total }) => {
  const steps = Array.from({ length: total }, (_, i) => i + 1);
  return (
    <div style={S.progress}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div style={S.progressStep(s === step, s < step)}>
            {s < step ? "✓" : s}
          </div>
          {i < steps.length - 1 && <div style={S.progressLine(s < step)} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── confirm dialog ───────────────────────────────────────────────────────────
const Confirm = ({ msg, onYes, onNo }) => (
  <Modal maxWidth={420}>
    <div style={{ textAlign: "center", padding: "1rem 0" }}>
      <div style={{ fontSize: 40, marginBottom: "1rem" }}>❓</div>
      <p style={{ color: "#e2e8f0", fontSize: 16, marginBottom: "1.5rem", lineHeight: 1.6 }}>{msg}</p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        <button style={S.btn("#1a7a4a", "#16a34a")} onClick={onYes}>Yego</button>
        <button style={S.btn("#b91c1c", "#dc2626")} onClick={onNo}>Oya</button>
      </div>
    </div>
  </Modal>
);

// ─── running totals ───────────────────────────────────────────────────────────
const Totals = ({ rows }) => {
  const t = rows.reduce((a, r) => ({
    elderly: a.elderly + (r.elderly_count || 0),
    children: a.children + (r.children_count || 0),
    youth: a.youth + (r.youth_count || 0),
    households: a.households + (r.households_count || 0),
    population: a.population + (r.total_population || 0),
  }), { elderly: 0, children: 0, youth: 0, households: 0, population: 0 });

  const items = [
    { label: "Abakuye", val: t.elderly, icon: "👴" },
    { label: "Abana", val: t.children, icon: "👶" },
    { label: "Urubyiruko", val: t.youth, icon: "🧑" },
    { label: "Ingo", val: t.households, icon: "🏠" },
    { label: "Abaturage Bose", val: t.population, icon: "👥" },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8, marginTop: "1rem" }}>
      {items.map(({ label, val, icon }) => (
        <div key={label} style={{ background: "rgba(0,53,128,0.25)", border: "1px solid rgba(200,168,75,0.2)", borderRadius: 10, padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: 22 }}>{icon}</div>
          <div style={{ color: "#c8a84b", fontWeight: 800, fontSize: 18 }}>{val.toLocaleString()}</div>
          <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>{label}</div>
        </div>
      ))}
    </div>
  );
};

// ─── isibo table ──────────────────────────────────────────────────────────────
const IsiboTable = ({ rows, onEdit, onDelete, readOnly }) => {
  const [hovered, setHovered] = useState(null);
  if (!rows.length) return (
    <div style={{ color: "#64748b", textAlign: "center", padding: "2rem", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 10, marginTop: "1rem" }}>
      Nta sibo yashyizweho.
    </div>
  );

  return (
    <div style={S.tableWrap} id="isibo-print-table">
      <table style={S.table}>
        <thead>
          <tr>
            {["Isibo", "Abakuye", "Abana", "Urubyiruko", "Ingo", "Abaturage Bose", "Status", ...(readOnly ? [] : ["Ibikorwa"])].map((h) => (
              <th key={h} style={S.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
              style={hovered === i ? S.trHover : {}}>
              <td style={S.td}>{r.isibo_name}</td>
              <td style={S.td}>{r.elderly_count}</td>
              <td style={S.td}>{r.children_count}</td>
              <td style={S.td}>{r.youth_count}</td>
              <td style={S.td}>{r.households_count}</td>
              <td style={{ ...S.td, fontWeight: 700, color: "#c8a84b" }}>{r.total_population}</td>
              <td style={S.td}>
                <span style={S.badge(r.status === "SUBMITTED" ? "#1a7a4a" : r.status === "APPROVED" ? "#c8a84b" : r.status === "REJECTED" ? "#b91c1c" : "#1a56b0")}>
                  {r.status}
                </span>
              </td>
              {!readOnly && (
                <td style={S.td}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {r.status === "DRAFT" && (
                      <>
                        <button style={S.btnSm("#1a56b0")} onClick={() => onEdit(r)}>✎ Hindura</button>
                        <button style={S.btnSm("#b91c1c")} onClick={() => onDelete(r.id)}>✕ Siba</button>
                      </>
                    )}
                    {r.status !== "DRAFT" && <span style={{ color: "#475569", fontSize: 11 }}>Ifunze</span>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── isibo form ───────────────────────────────────────────────────────────────
const emptyIsibo = { isibo_name: "", elderly_count: "", children_count: "", youth_count: "", households_count: "", total_population: "" };

const IsiboForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || emptyIsibo);
  const [errs, setErrs] = useState({});

  const set = (k) => (e) => {
    setForm((p) => ({ ...p, [k]: e.target.value }));
    setErrs((p) => ({ ...p, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.isibo_name.trim()) e.isibo_name = "Izina ry'Isibo rirakenewe";
    ["elderly_count", "children_count", "youth_count", "households_count", "total_population"].forEach((k) => {
      if (form[k] === "" || form[k] === null || form[k] === undefined) e[k] = "Iri gamba rirakenewe";
      else if (numVal(form[k]) === null) e[k] = "Shyiramo umubare wemewe (≥0)";
    });
    return e;
  };

  const submit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrs(e); return; }
    onSave({
      ...form,
      elderly_count: Number(form.elderly_count),
      children_count: Number(form.children_count),
      youth_count: Number(form.youth_count),
      households_count: Number(form.households_count),
      total_population: Number(form.total_population),
    });
  };

  const numFields = [
    { k: "elderly_count", label: "Umubare w'Abari mu Zabukuru" },
    { k: "children_count", label: "Umubare w'Abana" },
    { k: "youth_count", label: "Umubare w'Urubyiruko" },
    { k: "households_count", label: "Umubare w'Ingo" },
    { k: "total_population", label: "Umubare w'Abaturage Bose Batuye muri Iyo Sibo" },
  ];

  return (
    <div>
      <Field label="Izina ry'Isibo" error={errs.isibo_name}>
        <Input value={form.isibo_name} onChange={set("isibo_name")} placeholder="Injiza izina ry'isibo..." error={errs.isibo_name} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "0.75rem" }}>
        {numFields.map(({ k, label }) => (
          <Field key={k} label={label} error={errs[k]}>
            <NumInput value={form[k]} onChange={set(k)} placeholder="0" error={errs[k]} />
          </Field>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: "1rem", flexWrap: "wrap" }}>
        <button style={S.btn()} onClick={submit}>💾 Bika Amakuru</button>
        {onCancel && <button style={S.btn("#475569", "#64748b")} onClick={onCancel}>Gusubira Inyuma</button>}
      </div>
    </div>
  );
};

// ─── VILLAGE WIZARD ───────────────────────────────────────────────────────────
const TOTAL_STEPS = 5;
// step 1: village name | step 2: isibo form | step 3: list + add more | step 4: summary | step 5: done

const VillageWizard = ({ onClose, currentUser }) => {
  const [store, update] = useStore();
  const [step, setStep] = useState(1);
  const [villageName, setVillageName] = useState("");
  const [villageErr, setVillageErr] = useState("");
  const [currentVillageId, setCurrentVillageId] = useState(null);
  const [editingIsibo, setEditingIsibo] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const [note, setNote] = useState("");

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const currentVillage = useMemo(() => store.villages.find((v) => v.id === currentVillageId), [store.villages, currentVillageId]);
  const villageIsibo = useMemo(() => store.isibo.filter((i) => i.village_id === currentVillageId), [store.isibo, currentVillageId]);

  // ── step 1: create village ─────────────────────────────────────────────────
  const handleVillageContinue = () => {
    if (!villageName.trim()) { setVillageErr("Izina ry'Umudugudu rirakenewe"); return; }
    const existing = store.villages.find((v) => v.village_name.toLowerCase() === villageName.trim().toLowerCase());
    if (existing) {
      setCurrentVillageId(existing.id);
      showToast(`Umudugudu "${existing.village_name}" wabonetse.`, "info");
    } else {
      const village = { id: uid(), village_name: villageName.trim(), created_by: currentUser || "User", created_at: now(), status: "ACTIVE" };
      update((d) => { d.villages.push(village); d.logs.push({ id: uid(), action: "CREATE_VILLAGE", detail: village.village_name, user: village.created_by, at: now() }); return d; });
      setCurrentVillageId(village.id);
    }
    setStep(2);
  };

  // ── step 2: save isibo ────────────────────────────────────────────────────
  const handleSaveIsibo = (form) => {
    if (editingIsibo) {
      update((d) => {
        const i = d.isibo.findIndex((x) => x.id === editingIsibo.id);
        if (i !== -1) d.isibo[i] = { ...d.isibo[i], ...form, updated_at: now() };
        d.logs.push({ id: uid(), action: "EDIT_ISIBO", detail: form.isibo_name, user: currentUser || "User", at: now() });
        return d;
      });
      setEditingIsibo(null);
      showToast("Isibo yahindutse neza.");
    } else {
      const rec = { id: uid(), village_id: currentVillageId, ...form, status: "DRAFT", created_by: currentUser || "User", created_at: now(), submitted_at: null };
      update((d) => { d.isibo.push(rec); d.logs.push({ id: uid(), action: "ADD_ISIBO", detail: form.isibo_name, user: currentUser || "User", at: now() }); return d; });
      showToast("Amakuru y'Isibo yabitswe neza.");
    }
    setStep(3);
  };

  const handleDeleteIsibo = (id) => {
    update((d) => { d.isibo = d.isibo.filter((x) => x.id !== id); return d; });
    showToast("Isibo isibwe.", "info");
  };

  // ── submit to akagari ─────────────────────────────────────────────────────
  const handleSubmit = () => {
    const ts = now();
    update((d) => {
      d.isibo = d.isibo.map((r) => r.village_id === currentVillageId && r.status === "DRAFT"
        ? { ...r, status: "SUBMITTED", submitted_at: ts }
        : r
      );
      d.submissions.push({ id: uid(), village_id: currentVillageId, village_name: currentVillage?.village_name, submitted_by: currentUser || "User", submitted_at: ts, note, isibo_count: villageIsibo.length });
      d.logs.push({ id: uid(), action: "SUBMIT_TO_AKAGARI", detail: currentVillage?.village_name, user: currentUser || "User", at: ts });
      return d;
    });
    setConfirmSubmit(false);
    setStep(5);
    showToast("Amakuru yoherejwe ku Kagari neza.");
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal onClose={onClose}>
        {/* Header */}
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ color: "#c8a84b", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
            🏘 Tanga Amakuru y'Umudugudu Wawe
          </div>
          <ProgressBar step={step} total={TOTAL_STEPS} />
          <div style={{ color: "#94a3b8", fontSize: 12 }}>
            Intambwe {step} / {TOTAL_STEPS}
            {currentVillage && <span style={{ color: "#c8a84b", marginLeft: 10 }}>📍 {currentVillage.village_name}</span>}
          </div>
        </div>

        {/* ── STEP 1 ──────────────────────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <h3 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "1.25rem" }}>
              📋 Umwirondoro w'Umudugudu
            </h3>
            <Field label="Izina ry'Umudugudu" error={villageErr}>
              <Input value={villageName} onChange={(e) => { setVillageName(e.target.value); setVillageErr(""); }}
                placeholder="Injiza izina ry'umudugudu wawe..." error={villageErr}
                onKeyDown={(e) => e.key === "Enter" && handleVillageContinue()} />
            </Field>
            <button style={S.btn()} onClick={handleVillageContinue}>Komeza →</button>
          </div>
        )}

        {/* ── STEP 2 ──────────────────────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <h3 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "1.25rem" }}>
              {editingIsibo ? "✎ Hindura Isibo" : "➕ Andika Isibo Nshya"}
            </h3>
            <IsiboForm
              initial={editingIsibo}
              onSave={handleSaveIsibo}
              onCancel={villageIsibo.length > 0 ? () => { setEditingIsibo(null); setStep(3); } : null}
            />
          </div>
        )}

        {/* ── STEP 3 ──────────────────────────────────────────────────────── */}
        {step === 3 && (
          <div>
            <h3 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "0.5rem" }}>
              📊 Amasibo y'Umudugudu: <span style={{ color: "#c8a84b" }}>{currentVillage?.village_name}</span>
            </h3>
            <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: "0.75rem" }}>
              Ushobora gukomeza ku yindi Sibo cyangwa ukarangiza.
            </p>

            <IsiboTable
              rows={villageIsibo}
              onEdit={(r) => { setEditingIsibo(r); setStep(2); }}
              onDelete={handleDeleteIsibo}
            />
            <Totals rows={villageIsibo} />

            <hr style={S.divider} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button style={S.btn()} onClick={() => { setEditingIsibo(null); setStep(2); }}>
                ➕ Komeza ku Yindi Sibo
              </button>
              <button style={S.btn("#1a7a4a", "#16a34a")} onClick={() => setConfirmFinish(true)}
                disabled={!villageIsibo.length}>
                ✅ Narangije Amasibo Yose
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: summary ─────────────────────────────────────────────── */}
        {step === 4 && (
          <div>
            <h3 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "1rem" }}>
              📄 Incamake y'Amakuru Yose
            </h3>
            <div style={S.summaryBox}>
              <div style={{ color: "#c8a84b", fontWeight: 700, marginBottom: 6 }}>
                🏘 {currentVillage?.village_name}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>
                Byanditswe na: {currentVillage?.created_by} — {currentVillage?.created_at}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 2 }}>
                Amasibo: {villageIsibo.length}
              </div>
            </div>

            <IsiboTable rows={villageIsibo} readOnly />
            <Totals rows={villageIsibo} />

            <div style={{ marginTop: "1rem" }}>
              <Field label="Icyo Gusaba (ntibyakenewe)">
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  style={{ ...S.input, height: 70, resize: "vertical" }}
                  placeholder="Injiza icyo ushaka ko Akagari izi (ntibyakenewe)..." />
              </Field>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: "1rem", flexWrap: "wrap" }}>
              <button style={S.btn("#b45309", "#d97706")} onClick={() => exportCSV(villageIsibo.map((r) => ({ Isibo: r.isibo_name, Abakuye: r.elderly_count, Abana: r.children_count, Urubyiruko: r.youth_count, Ingo: r.households_count, Abaturage: r.total_population, Status: r.status })), `${currentVillage?.village_name}-isibo.csv`)}>
                📥 Excel
              </button>
              <button style={S.btn("#475569", "#64748b")} onClick={() => {
                const totals = villageIsibo.reduce((a,r)=>({elderly:a.elderly+(r.elderly_count||0),children:a.children+(r.children_count||0),youth:a.youth+(r.youth_count||0),households:a.households+(r.households_count||0),population:a.population+(r.total_population||0)}),{elderly:0,children:0,youth:0,households:0,population:0});
                printPDF(villageIsibo, store.villages, totals, `Raporo — ${currentVillage?.village_name}`);
              }}>
                🖨 PDF
              </button>
              <button style={S.btn("#1a7a4a", "#16a34a")} onClick={() => setConfirmSubmit(true)}>
                🚀 Oherereza Amakuru ku Kagari
              </button>
              <button style={S.btn("#475569", "#64748b")} onClick={() => setStep(3)}>← Gusubira Inyuma</button>
            </div>
          </div>
        )}

        {/* ── STEP 5: done ────────────────────────────────────────────────── */}
        {step === 5 && (
          <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
            <div style={{ fontSize: 64, marginBottom: "1rem" }}>🎉</div>
            <h3 style={{ color: "#c8a84b", fontWeight: 800, fontSize: 22, marginBottom: "0.5rem" }}>
              Byagenze Neza!
            </h3>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.7, marginBottom: "1.5rem" }}>
              Amakuru yoherejwe ku Kagari neza.<br />
              <strong style={{ color: "#f1f5f9" }}>{currentVillage?.village_name}</strong> — Amasibo: {villageIsibo.length}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button style={S.btn()} onClick={() => { setStep(1); setVillageName(""); setCurrentVillageId(null); }}>
                ➕ Tangira Umudugudu Mushya
              </button>
              <button style={S.btn("#475569", "#64748b")} onClick={onClose}>Funga</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm finish all isibo */}
      {confirmFinish && (
        <Confirm
          msg="Wasoje kuzuza amasibo yose ari mu mudugudu wawe?"
          onYes={() => { setConfirmFinish(false); setStep(4); }}
          onNo={() => setConfirmFinish(false)}
        />
      )}

      {/* Confirm submit */}
      {confirmSubmit && (
        <Confirm
          msg={`Oherereza amasibo yose ya "${currentVillage?.village_name}" ku Kagari? Ntibishobora guhindurwa nyuma.`}
          onYes={handleSubmit}
          onNo={() => setConfirmSubmit(false)}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <style>{`@keyframes slideUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </>
  );
};

// ─── AKAGARI DASHBOARD ────────────────────────────────────────────────────────
const AkagariVillageBoard = ({ onClose }) => {
  const [store, update] = useStore();
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  useEffect(() => {
    setStatsLoading(true);
    axiosInstance.get("/akagari/stats")
      .then(r => setApiStats(r.data))
      .catch(() => setApiStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const submitted = useMemo(() => store.isibo.filter((i) => i.status === "SUBMITTED" || i.status === "APPROVED" || i.status === "REJECTED"), [store.isibo]);

  const villages = useMemo(() => {
    const ids = [...new Set(submitted.map((i) => i.village_id))];
    return ids.map((vid) => {
      const v = store.villages.find((x) => x.id === vid);
      const rows = submitted.filter((i) => i.village_id === vid);
      return { ...v, isibo: rows };
    }).filter((v) => v.village_name?.toLowerCase().includes(search.toLowerCase()));
  }, [submitted, store.villages, search]);

  const grandTotals = useMemo(() => submitted.reduce((a, r) => ({
    elderly: a.elderly + (r.elderly_count || 0),
    children: a.children + (r.children_count || 0),
    youth: a.youth + (r.youth_count || 0),
    households: a.households + (r.households_count || 0),
    population: a.population + (r.total_population || 0),
  }), { elderly: 0, children: 0, youth: 0, households: 0, population: 0 }), [submitted]);

  const approve = (isiboId) => {
    update((d) => {
      const i = d.isibo.findIndex((x) => x.id === isiboId);
      if (i !== -1) { d.isibo[i].status = "APPROVED"; d.isibo[i].reviewed_at = now(); }
      d.logs.push({ id: uid(), action: "APPROVE_ISIBO", detail: isiboId, user: "Akagari", at: now() });
      return d;
    });
    showToast("Isibo yemejwe.");
  };

  const reject = (isiboId) => {
    update((d) => {
      const i = d.isibo.findIndex((x) => x.id === isiboId);
      if (i !== -1) { d.isibo[i].status = "REJECTED"; d.isibo[i].reject_note = rejectNote; d.isibo[i].reviewed_at = now(); }
      d.logs.push({ id: uid(), action: "REJECT_ISIBO", detail: isiboId, user: "Akagari", at: now() });
      return d;
    });
    setRejectTarget(null); setRejectNote("");
    showToast("Isibo yasubijwe ngo ikosorwe.", "error");
  };

  const tabs = [
    { id: "overview", label: "📊 Overview" },
    { id: "villages", label: "🏘 Imidugudu" },
    { id: "isibo", label: "📋 Amasibo Yose" },
    { id: "logs", label: "📝 Audit Trail" },
  ];

  const tabStyle = (id) => ({
    padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
    background: tab === id ? "#003580" : "rgba(255,255,255,0.05)",
    color: tab === id ? "#fff" : "#94a3b8",
    transition: "all .2s",
  });

  return (
    <Modal onClose={onClose} maxWidth={1000}>
      <div style={{ color: "#c8a84b", fontWeight: 800, fontSize: 18, marginBottom: "1rem" }}>
        🏛 Akagari — Amakuru y'Imidugudu
      </div>

      {/* tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button key={t.id} style={tabStyle(t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button style={S.btnSm("#b45309")} onClick={() => exportCSV(submitted.map((r) => ({ Village: store.villages.find((v) => v.id === r.village_id)?.village_name, Isibo: r.isibo_name, Abakuye: r.elderly_count, Abana: r.children_count, Urubyiruko: r.youth_count, Ingo: r.households_count, Abaturage: r.total_population, Status: r.status, Yoherejweho: r.submitted_at })), "akagari-raporo.csv")}>
            📥 Excel
          </button>
          <button style={S.btnSm("#475569")} onClick={() => printPDF(submitted, store.villages, grandTotals, "Raporo — Akagari")}>🖨 PDF</button>
        </div>
      </div>

      {/* ── overview ────────────────────────────────────────────────────────── */}
      {tab === "overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: "1.25rem" }}>
            {[
              { label: "Imidugudu", val: statsLoading ? "…" : (apiStats?.imidugudu ?? 0), icon: "🏘", color: "#003580" },
              { label: "Abaturage Bose", val: statsLoading ? "…" : (apiStats?.abaturage ?? 0).toLocaleString(), icon: "👥", color: "#c8a84b" },
              { label: "Ingo", val: statsLoading ? "…" : (apiStats?.ingo ?? 0).toLocaleString(), icon: "🏠", color: "#1a7a4a" },
              { label: "Urubyiruko", val: statsLoading ? "…" : (apiStats?.urubyiruko ?? 0).toLocaleString(), icon: "🧑", color: "#1a56b0" },
              { label: "Abana", val: statsLoading ? "…" : (apiStats?.abana ?? 0).toLocaleString(), icon: "👶", color: "#003580" },
            ].map(({ label, val, icon, color }) => (
              <div key={label} style={{ background: "rgba(0,53,128,0.2)", border: "1px solid rgba(200,168,75,0.15)", borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 26 }}>{icon}</div>
                <div style={{ color, fontWeight: 800, fontSize: 20 }}>{val}</div>
                <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.summaryBox, marginTop: 0 }}>
            <div style={{ color: "#c8a84b", fontWeight: 700, marginBottom: 8 }}>Submissions Zaheruka</div>
            {store.submissions.slice(-5).reverse().map((s) => (
              <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 13 }}>
                <span style={{ color: "#f1f5f9" }}>{s.village_name}</span>
                <span style={{ color: "#94a3b8" }}>{s.isibo_count} isibo</span>
                <span style={{ color: "#64748b", fontSize: 11 }}>{s.submitted_at}</span>
              </div>
            ))}
            {!store.submissions.length && <div style={{ color: "#64748b" }}>Nta amakuru yoherejwe.</div>}
          </div>
        </div>
      )}

      {/* ── villages ─────────────────────────────────────────────────────────── */}
      {tab === "villages" && (
        <div>
          <div style={{ marginBottom: "1rem" }}>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Shakisha umudugudu..." />
          </div>
          {villages.map((v) => (
            <div key={v.id} style={{ ...S.summaryBox, cursor: "pointer", marginBottom: "0.75rem" }}
              onClick={() => setSelectedVillage(selectedVillage?.id === v.id ? null : v)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ color: "#f1f5f9", fontWeight: 700 }}>{v.village_name}</span>
                  <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 10 }}>Amasibo: {v.isibo.length}</span>
                </div>
                <span style={{ color: "#c8a84b", fontSize: 12 }}>Yoherejweho na {v.isibo[0]?.submitted_at || "—"}</span>
              </div>
              {selectedVillage?.id === v.id && (
                <div style={{ marginTop: "0.75rem" }}>
                  <IsiboTable rows={v.isibo} readOnly />
                  <Totals rows={v.isibo} />
                </div>
              )}
            </div>
          ))}
          {!villages.length && <div style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>Nta imidugudu yoherejwe.</div>}
        </div>
      )}

      {/* ── all isibo ─────────────────────────────────────────────────────────── */}
      {tab === "isibo" && (
        <div id="akagari-print-all">
          <div style={{ marginBottom: "1rem" }}>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Shakisha isibo cyangwa umudugudu..." />
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Umudugudu", "Isibo", "Abakuye", "Abana", "Urubyiruko", "Ingo", "Abaturage", "Status", "Yoherejweho", "Uwayohereje", "Ibikorwa"].map((h) => (
                    <th key={h} style={{ ...S.th, ...(h === "Ibikorwa" ? { color: "#fff", textAlign: "center", minWidth: 180 } : {}) }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submitted
                  .filter((r) => {
                    const vname = store.villages.find((v) => v.id === r.village_id)?.village_name || "";
                    return vname.toLowerCase().includes(search.toLowerCase()) || r.isibo_name.toLowerCase().includes(search.toLowerCase());
                  })
                  .map((r) => {
                    const vname = store.villages.find((v) => v.id === r.village_id)?.village_name || "—";
                    return (
                      <tr key={r.id}>
                        <td style={S.td}>{vname}</td>
                        <td style={S.td}>{r.isibo_name}</td>
                        <td style={S.td}>{r.elderly_count}</td>
                        <td style={S.td}>{r.children_count}</td>
                        <td style={S.td}>{r.youth_count}</td>
                        <td style={S.td}>{r.households_count}</td>
                        <td style={{ ...S.td, fontWeight: 700, color: "#c8a84b" }}>{r.total_population}</td>
                        <td style={S.td}>
                          <span style={S.badge(r.status === "APPROVED" ? "#1a7a4a" : r.status === "REJECTED" ? "#b91c1c" : "#1a56b0")}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ ...S.td, fontSize: 11 }}>{r.submitted_at}</td>
                        <td style={{ ...S.td, fontSize: 11 }}>{r.created_by}</td>
                        <td style={{ ...S.td, minWidth: 180 }}>
                          {r.status === "SUBMITTED" && (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <button
                                style={S.btnSm("#16a34a")}
                                onClick={() => approve(r.id)}
                                onMouseEnter={e => { e.currentTarget.style.background = "#15803d"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "#16a34a"; e.currentTarget.style.transform = "none"; }}
                              >
                                ✓ Emeza
                              </button>
                              <button
                                style={S.btnSm("#dc2626")}
                                onClick={() => setRejectTarget(r.id)}
                                onMouseEnter={e => { e.currentTarget.style.background = "#b91c1c"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.transform = "none"; }}
                              >
                                ✕ Saba Gukosora
                              </button>
                            </div>
                          )}
                          {r.status === "APPROVED" && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(22,163,74,0.15)", border: "1px solid rgba(22,163,74,0.4)", borderRadius: 20, padding: "3px 12px", color: "#4ade80", fontSize: 12, fontWeight: 700 }}>
                              ✓ Yemejwe
                            </span>
                          )}
                          {r.status === "REJECTED" && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.35)", borderRadius: 20, padding: "3px 12px", color: "#f87171", fontSize: 12, fontWeight: 700 }} title={r.reject_note}>
                              ✕ Yagaruwe
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── audit logs ─────────────────────────────────────────────────────────── */}
      {tab === "logs" && (
        <div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Igihe", "Igikorwa", "Ibindi", "Uwakoreye"].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...store.logs].reverse().map((l) => (
                  <tr key={l.id}>
                    <td style={{ ...S.td, fontSize: 11 }}>{l.at}</td>
                    <td style={S.td}><span style={S.badge("#1a56b0")}>{l.action}</span></td>
                    <td style={S.td}>{l.detail}</td>
                    <td style={S.td}>{l.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!store.logs.length && <div style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>Nta makuru asigaje.</div>}
        </div>
      )}

      {/* reject modal */}
      {rejectTarget && (
        <Modal maxWidth={440}>
          <h4 style={{ color: "#f1f5f9", marginBottom: "1rem" }}>✕ Andika Icyo Gukosora</h4>
          <Field label="Impamvu yo Gusubiza">
            <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
              style={{ ...S.input, height: 90, resize: "vertical" }}
              placeholder="Andika impamvu ndetse n'icyo gukosora..." />
          </Field>
          <div style={{ display: "flex", gap: 10, marginTop: "1rem" }}>
            <button style={S.btn("#b91c1c", "#dc2626")} onClick={() => reject(rejectTarget)}>Ohereza</button>
            <button style={S.btn("#475569", "#64748b")} onClick={() => { setRejectTarget(null); setRejectNote(""); }}>Gusubira Inyuma</button>
          </div>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </Modal>
  );
};

// ─── NAV BUTTONS (exported for use in nav bars) ───────────────────────────────
export const VillageNavButton = ({ role = "mudugudu", currentUser, style: extraStyle }) => {
  const [open, setOpen] = useState(false);

  if (role === "akagari") {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          style={{ ...S.btn("#003580", "#1a56b0"), fontSize: 13, padding: "8px 16px", borderRadius: 10, ...(extraStyle || {}) }}
        >
          🏛 Amakuru y'Imidugudu
        </button>
        {open && <AkagariVillageBoard onClose={() => setOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ ...S.btn("#003580", "#1a56b0"), fontSize: 13, padding: "8px 16px", borderRadius: 10, ...(extraStyle || {}) }}
      >
        🏘 Tanga Amakuru y'Umudugudu Wawe
      </button>
      {open && <VillageWizard onClose={() => setOpen(false)} currentUser={currentUser} />}
    </>
  );
};

// ─── STANDALONE PAGE (route: /village-info) ──────────────────────────────────
const VillageInfoSystem = () => {
  const [mode, setMode] = useState(null); // null | "mudugudu" | "akagari"
  const currentUser = localStorage.getItem("fullname") || localStorage.getItem("username") || "User";
  const role = localStorage.getItem("role") || "";
  const isAkagari = role.toLowerCase().includes("akagari");

  if (mode === "mudugudu") return <VillageWizard onClose={() => setMode(null)} currentUser={currentUser} />;
  if (mode === "akagari") return <AkagariVillageBoard onClose={() => setMode(null)} />;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#000d24,#001845)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1rem" }}>
      <div style={{ textAlign: "center", maxWidth: 560, width: "100%" }}>
        <div style={{ fontSize: 64, marginBottom: "1rem" }}>🏘</div>
        <h1 style={{ color: "#c8a84b", fontWeight: 800, fontSize: 26, marginBottom: "0.5rem" }}>
          Sisitemu y'Amakuru y'Umudugudu
        </h1>
        <p style={{ color: "#94a3b8", fontSize: 15, marginBottom: "2rem", lineHeight: 1.7 }}>
          Hitamo urubuga ukurikije inshingano zawe
        </p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <button style={{ ...S.btn(), padding: "14px 28px", fontSize: 15 }} onClick={() => setMode("mudugudu")}>
            👥 Umudugudu — Tanga Amakuru
          </button>
          <button style={{ ...S.btn("#1a7a4a", "#16a34a"), padding: "14px 28px", fontSize: 15 }} onClick={() => setMode("akagari")}>
            🏛 Akagari — Reba Amakuru
          </button>
        </div>
      </div>
    </div>
  );
};

export default VillageInfoSystem;
